import React, { FC, memo, useCallback } from 'react';
import styled from 'styled-components';
import { View, ViewType } from 'api/views/types';
import { confirm } from 'components/atoms/modal';
import { ManageViewDrawer } from 'components/molecules/views/manage-view-drawer';
import { AllViews } from 'components/molecules/views/saved-views/views';
import { useViewActions } from 'components/molecules/views/view-actions';
import { FilterableField } from 'components/organisms/tasks/fields';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { useTranslate } from 'hooks/use-translation';
import { useUpdateTaskViewMutation } from 'queries/task-view';
import { ColumnOrder } from '../grid-custom-column';
import { GroupedFilter } from '../tasks/grouped-filter-popover';
import { GroupBy } from './use-group-by-hash';
import { useGroupedFilterMenu } from './use-grouped-filter-menu';

interface Props {
  filter: GroupedFilter;
  isFieldsLoading: boolean;
  fields: FilterableField[];
  lookup: GroupFieldsMeta;
  taskLookup: Record<string, FilterableField>;
  selectFilter: (filter: GroupedFilter) => void;
  clearFilters: () => void;
  groupBy?: GroupBy;
  setGroupBy?: (value: GroupBy, replace?: boolean) => void;
  columnOrder?: ColumnOrder[];
  handleColumnOrderChange?: (changedOrder: ColumnOrder[]) => void;
  defaultColumnOrder?: ColumnOrder[];
  isResizing?: boolean;
}

export const Sider = styled.div<{ isResizing: boolean }>`
  /* transition: all 0.1s cubic-bezier(0.29, 0.04, 0.23, 1); */
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  transition: ${({ isResizing }) =>
    isResizing ? 'width 0s' : 'all 0.3s cubic-bezier(0.29, 0.04, 0.23, 1)'};
`;

const _GroupFilterMenu: FC<Props> = ({
  filter,
  lookup,
  taskLookup,
  fields,
  isFieldsLoading,
  selectFilter,
  clearFilters,
  groupBy,
  setGroupBy,
  columnOrder = [],
  handleColumnOrderChange = () => {},
  defaultColumnOrder = [],
  isResizing = false,
}) => {
  const translate = useTranslate();
  const {
    taskListViews,
    viewDefinitionTransformer,
    currentViewId,
    onViewSelect,
    defaultViewsList,
    defaultViewId,
    manageViewModalVisible,
    setManageViewModalVisible,
    setCurrentEditingView,
    currentEditingView,
    selectedView,
    onViewEdit,
    isViewsLoading,
    onDeleteSuccess,
    onDeleteFailure,
    defaultSharedFilter,
    viewContextValue,
  } = useGroupedFilterMenu({
    fields,
    lookup,
    isFieldsLoading,
    selectFilter,
    clearFilters,
    setGroupBy,
    taskLookup,
    filter,
    handleColumnOrderChange,
    defaultColumnOrder,
  });

  const { onUpdate: onTaskViewUpdate } = useUpdateTaskViewMutation();

  const { onViewUpdate, onViewDelete } = useViewActions({
    views: taskListViews,
    setViews: onTaskViewUpdate,
    viewType: ViewType.AllTasks,
    transformer: viewDefinitionTransformer,
  });

  const onDelete = useCallback(
    (view: View<any>) => {
      confirm({
        okText: translate('delete'),
        okType: 'danger',
        cancelText: translate('cancel'),
        onCancel: () => {},
        title: translate('views.delete_confirmation.title'),
        content: (
          <>
            {!view.viewFilterShareConfig?.shareWithProjects ? (
              translate('views.delete_confirmation.content', {
                viewName: view.viewName,
                strong: (chunks) => <strong>{chunks}</strong>,
              })
            ) : (
              <>
                {translate('views.also.available.in', {
                  viewName: view.viewName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
                <ul>
                  {view.viewFilterShareConfig?.sharedWithCustomerPortal
                    ? translate('views.found.in', {
                        li: (chunks) => <li>{chunks}</li>,
                      })
                    : translate('views.found.in.only.project.list', {
                        li: (chunks) => <li>{chunks}</li>,
                      })}
                </ul>
                <div>
                  {translate('views.update.from.everywhere')}
                  &nbsp;
                </div>
                <div>{translate('views.delete_confirmation.text')}</div>
              </>
            )}
          </>
        ),
        onOk: () => {
          if (view.viewId === currentViewId) {
            onViewSelect(defaultViewsList.byIds[defaultViewId][1]);
          }

          onViewDelete(view)
            .then(() => onDeleteSuccess(view))
            .catch(() => onDeleteFailure(view));
        },
      });
    },
    [
      translate,
      currentViewId,
      onViewSelect,
      defaultViewsList.byIds,
      defaultViewId,
      onDeleteSuccess,
      onDeleteFailure,
      onViewDelete,
    ],
  );

  return (
    <Sider isResizing={isResizing}>
      <ManageViewDrawer
        visible={manageViewModalVisible}
        onClose={() => {
          setManageViewModalVisible(false);
          setCurrentEditingView(undefined);
        }}
        view={currentEditingView}
        onViewUpdate={onViewUpdate}
        currentViewDefinition={{
          filter,
          groupBy,
          columnOrder,
        }}
        defaultSharedFilter={defaultSharedFilter}
      />
      <AllViews
        contextValue={viewContextValue}
        views={taskListViews}
        defaultViewsList={defaultViewsList}
        defaultViewId={defaultViewId}
        onViewSelect={onViewSelect}
        selectedView={selectedView}
        onViewEdit={onViewEdit}
        onViewDelete={onDelete}
        loading={isViewsLoading}
      />
    </Sider>
  );
};

export const GroupFilterMenu = memo(_GroupFilterMenu);
